import { Permissions } from '../../../../models/enums/permissions';
import { cloneDeep } from 'lodash';
import * as React from 'react';
import { DataGroupHeader } from '../../../../components/data-group/DataGroupHeader';
import * as Forms from '../../../../components/form-elements/_index';
import { PageActions } from '../../../../components/form-elements/FormSave';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { StateListDisplayNames } from '../../../../components/utility/states';
import { useRouteMiss } from '../../../../hooks/use-route-miss';
import { useServiceCenter } from '../../../../hooks/use-service-center';
import { StatusCode } from '../../../../utils/api';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { toShortDate } from '../../../../utils/date-utils';
import { navigateToPage } from '../../../../utils/history';
import * as transportActions from '../../../../transportation/actions';
import { FleetActionEditDriver } from '../components/FleetActionEditDriver';
import { DriverStatus } from '../../../../transportation/models/enums/driver-status';
import { DriverRead } from '../../../../transportation/models/fleet/driver-read';
import { TransportationSvc } from '../../../../transportation/service';

interface IProps {
    location: any;
    match: any;
}

export const EditDriverScene = (props : IProps) => {

    const userServiceCenter                     = useServiceCenter();
    const [service]                             = React.useState(new TransportationSvc());
    const [driver, setDriver]                   = React.useState(new DriverRead());
    const [editDriver, setEditDriver]           = React.useState({} as DriverRead);
    const [showInfoFlyout, setShowInfoFlyout]   = React.useState(false);

    useRouteMiss(
        Routes.SHIPPING_FLEET_DRIVERS,
        transportActions.clearDriverSearch()
    );

    /**
     * Go fetch the provided driver.  If no ID is passed in
     * new up an DriverRead for editing purposes
     * @param driverId The optional driver to go fetch
     */
    async function getDriver(driverId: number) {

        if (driverId) {
            const response = await service.getDriver(driverId);
            if (response.status === StatusCode.Success) {
                setDriver(response.data);
                return;
            }
        }
       
        const d = new DriverRead();
        if (userServiceCenter.name !== 'ALL') {
            d.serviceCenterId = userServiceCenter.name;
        }

        setDriver(d);
        onFlyoutShow(d);
    }

    /**
     * Respond to changes in the location
     */
    React.useEffect(() => {
        const opts = props.location.pathname.split(Routes.SHIPPING_FLEET_DRIVERS_EDIT + '/');
        getDriver(opts[1]);

    }, [props.location]);

    const onFlyoutShow = (d : DriverRead) => {
        const cloned = (d && d.serviceCenterId) ? cloneDeep(d) : cloneDeep(driver);
        setEditDriver(cloned);
        setShowInfoFlyout(true);
    };

    const onFlyoutSuccess = async (result : DriverRead) => {
        if (result && driver.id) {            
            setEditDriver({} as unknown as DriverRead);
            setShowInfoFlyout(false);
            await getDriver(driver.id);
        }
        else if (result) {
            setEditDriver({} as unknown as DriverRead);
            setShowInfoFlyout(false);
            navigateToPage(Routes.SHIPPING_FLEET_DRIVERS_EDIT_WITH_ID.formattify(result.id));
        }
    };

    const onFlyoutClose = async () => {
        setShowInfoFlyout(false);
    };

    return !AppUtils.IsEmpty(driver)
            ? <PageContent classes="edit-driver is-in-tabs" pageName={PageNames.TRANSPORTATION_FLEET_DRIVERS_DETAILS}>
                <PageHeading 
                    shouldStick={true} 
                    showBackBtn={ true }
                    onBackClick={ () => navigateToPage(Routes.SHIPPING_FLEET_DRIVERS) }
                    title={ driver.id > 0 ? `${driver.firstName} ${driver.lastName}` : 'Create Driver' }>            
                    <PageActions
                        id="driver-edit-form"
                        onEdit={ onFlyoutShow }
                        editPermissions={[
                            Permissions.ShippingFleetDriversCreate,
                            Permissions.ShippingFleetDriversEdit
                        ]}
                        deletePermissions={[]}
                        onDelete={ () => { return; }}
                    />                    
                </PageHeading>
                <PageBody>                
                    <div className="row">
                        <div className="col-12">
                            <DataGroupHeader title="Basic Information" />
                        </div>
                    </div>
                    <div className="row">                    
                        <div className="col-6">
                            <Forms.ReadView
                                label="First name"
                                value={ driver.firstName }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Middle name"
                                value={ driver.middleName }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Last name"
                                value={ driver.lastName }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Employee ID"
                                value={ driver.employeeId }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Service Center"
                                value={ driver.serviceCenterId }
                            />
                        </div> 
                        <div className="col-6">
                            <Forms.ReadView
                                label="Status"
                                value={
                                    driver.status === DriverStatus.Active   
                                        ? <span className="driver-status active">ACTIVE</span>
                                        : <span className="driver-status inactive">INACTIVE</span>
                                }
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <DataGroupHeader title="Phone Information" />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <Forms.ReadView
                                label="Cell Phone #"
                                value={ driver.cellPhone }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Cell Phone # (Alt.)"
                                value={ driver.alternatePhone }
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <DataGroupHeader title="License Information" />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <Forms.ReadView
                                label="Driver's License Number"
                                value={ driver.driversLicenseNumber }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Driver's License State"
                                value={ StateListDisplayNames[driver.driversLicenseState] }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Driver's License Expiry"
                                value={ driver.driversLicenseExpiry ? toShortDate(driver.driversLicenseExpiry) : 'Not Provided' }
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <DataGroupHeader title="Additional Information" />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <Forms.ReadView
                                label="Default Truck"
                                value={ driver.truckName }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Passport ID"
                                value={ driver.passportId }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Physical Due Date"
                                value={ driver.physicalDueDate ? toShortDate(driver.physicalDueDate) : 'Not Provided' }
                            />
                        </div>
                        <div className="col-6">
                            <Forms.ReadView
                                label="Samsara ID"
                                value={ driver.samsaraId }
                            />
                        </div>

                    </div>
                 
                    <FleetActionEditDriver
                        visible={ showInfoFlyout }
                        onSuccess={ onFlyoutSuccess }
                        onClose={ onFlyoutClose }
                        driver={ editDriver }
                    />

                </PageBody>
            </PageContent> 
            : null;

};
